import { useEffect, useState, Suspense, lazy } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import LoadingComponent from "./components/Loading";
import { getLoggedIn, logout } from "./services/auth";
import routes from "./config/routes";
import * as USER_HELPERS from "./utils/userToken";
import * as PATHS from "./utils/paths";
import { SearchProvider } from "./components/SearchBar/SearchContext";
import { JobProvider } from "./pages/Protected/Jobs/JobsContext";

// Lazy Loaded Components
const HomePage = lazy(() => import("./pages/Auth/HomePage"));
const Signup = lazy(() => import("./pages/Auth/Signup"));
const Login = lazy(() => import("./pages/Auth/LogIn"));
const ProtectedPage = lazy(() => import("./pages/Protected/ProtectedPage"));
const JobList = lazy(() => import("./pages/Protected/Jobs/JobList"));
const JobPostForm = lazy(() => import("./pages/Protected/CRUD/JobPostForm"));
const JobDetail = lazy(() => import("./pages/Protected/Jobs/JobDetail"));
const JobEditForm = lazy(() => import("./pages/Protected/CRUD/JobEditForm"));
const ClientDashboard = lazy(() => import("./pages/Client/ClientDashboard"));
const JobPreview = lazy(() => import("./pages/Protected/Jobs/JobPreview"));
const AboutPage = lazy(() => import("./pages/Client/AboutPage"));
const MobileClientDashboard = lazy(() => import("./pages/Client/MobileClientDashboard"));
const ServiceClients = lazy(() => import("./pages/Client/ServicesClients"));
const HomeClient = lazy(() => import("./pages/Client/HomeClient"));
const ClientPublicLayout = lazy(() => import("./components/ClientePublicLayout"));
const SubscriberStats = lazy(() => import("./pages/Protected/Stats/SubscriberStats"));


export default function App() {
  const isMobile = useMediaQuery({ query: "(max-width: 1388px)" });
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const accessToken = USER_HELPERS.getUserToken();
    if (!accessToken) {
      return setIsLoading(false);
    }
    getLoggedIn(accessToken).then((res) => {
      if (!res.status) {
        return setIsLoading(false);
      }
      setUser(res.data.user);
      setIsLoading(false);
    });
  }, []);

  // handling logout 
  function handleLogout() {
    const accessToken = USER_HELPERS.getUserToken();
    if (!accessToken) {
       // If there is no token, update user state and navigate to login
    setUser(null);
    setIsLoading(false);
    navigate(PATHS.LOGINPAGE);
    return;
      
    }
    setIsLoading(true);
 
    logout(accessToken)
    .then((res) => {
      if (!res.status) {
        // Handle unsuccessful logout (e.g., server issues)
        console.error("Logout was unsuccessful: ", res);
      } else {
        USER_HELPERS.removeUserToken(); // Remove session token from localStorage
        USER_HELPERS.removeJwtToken();   // Remove JWT from sessionStorage
        setUser(null);                   // Set user state to null
        navigate(PATHS.LOGINPAGE);       // Navigate to the login page
      }
    })
    .catch((err) => {
      console.error("Error during logout: ", err);
    })
    .finally(() => {
      // Always set loading to false once the logout request is complete
      setIsLoading(false);
    });
  }
  function authenticate(user) {
    setUser(user);
  }

  if (isLoading) {
    return <LoadingComponent />;
  }
  return (
    <div className="App">
      <Suspense fallback={<LoadingComponent />}>
        <Routes>

        {/* AUTH PAGES (Home, Login, Signup) */}
        <Route
          path={PATHS.HOMEPAGE}
          element={
            <HomePage
              user={user}
              authenticate={authenticate}
              handleLogout={handleLogout}
            />
          }
        >
          <Route index element={<Navigate to={PATHS.CLIENTDASHBOARD} />} />
          <Route path={PATHS.SIGNUPPAGE} element={<Signup authenticate={authenticate} />} />
          <Route path={PATHS.LOGINPAGE} element={<Login authenticate={authenticate} />} />
        </Route>

        {/* PROTECTED DASHBOARD */}
        <Route
          path="dashboard/*"
          element={
            <SearchProvider>
              <JobProvider>
                <ProtectedPage user={user} handleLogout={handleLogout} />
              </JobProvider>
            </SearchProvider>
          }
        >
          <Route path="jobs" element={<JobList />} />
          <Route path="job/:jobId" element={<JobDetail />} />
          <Route path="edit-job/:jobId" element={<JobEditForm />} />
          <Route path="post-job" element={<JobPostForm />} />
          <Route path="job-preview" element={<JobPreview />} />
          <Route path="estadisticas" element={<SubscriberStats />} />
        </Route>

        {/* PUBLIC CLIENT ROUTES (WITH NAVBAR) */}

        <Route element={<ClientPublicLayout />}>

          {/* Servicios a Clientes */}
          <Route path="/clientservice" element={<ServiceClients />} />

          {/* Client Dashboard Público */}
          <Route
            path="/clientdashboard/*"
            element={
              <SearchProvider>
                <JobProvider>
                  <HomeClient />
                </JobProvider>
              </SearchProvider>
            }
          >
            <Route index element={<Navigate to="about" />} />

            <Route
              path="clientjobs/:id?"
              element={
                isMobile ? <MobileClientDashboard /> : <ClientDashboard />
              }
            />

            <Route path="about" element={<AboutPage />} />
          </Route>

        </Route>

        {/* DYNAMIC ROUTES */}
        {routes({ user, authenticate, handleLogout }).map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}

        </Routes>
      </Suspense>
    </div>

  );
}

